'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  MapPin, Clock, Send, CheckCircle2, AlertCircle, Loader2
} from 'lucide-react';

const INQUIRY_TYPES = [
  'General Inquiry',
  'Sales',
  'Enterprise / Custom Plan',
  'Billing & Payments',
  'Invoice or VAT Request',
  'Support',
  'Technical Issue',
  'Report a Bug or Error',
  'Legal & Compliance',
  'Privacy & Data Request',
  'Partnership',
  'Affiliate & Referral Program',
  'Webinar Inquiry',
  'Feature Request',
  'Careers',
  'Media & Press',
];

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  inquiryType: string;
  message: string;
  whatTried: string;
  whatHappened: string;
  browser: string;
  device: string;
  screenshotUrl: string;
  gdprConsent: boolean;
  marketingConsent: boolean;
}

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  inquiryType?: string;
  message?: string;
  whatTried?: string;
  whatHappened?: string;
  gdprConsent?: string;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  fontSize: '14px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: 'white',
  outline: 'none',
  boxSizing: 'border-box',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  border: '1px solid rgba(239,68,68,0.5)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: '#94A3B8',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({
    firstName: '', lastName: '', email: '', company: '', phone: '',
    inquiryType: '', message: '', whatTried: '', whatHappened: '',
    browser: '', device: '', screenshotUrl: '',
    gdprConsent: false, marketingConsent: false,
  });
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState<{ ticketRef: string; email: string; guestToken?: string } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setForm(prev => ({
        ...prev,
        browser: navigator.userAgent,
        device: navigator.platform,
      }));
    }
  }, []);

  const isBugReport = form.inquiryType === 'Report a Bug or Error';

  function getErrors(): FieldErrors {
    const errs: FieldErrors = {};
    if (!form.firstName.trim()) errs.firstName = 'First Name is required';
    if (!form.lastName.trim()) errs.lastName = 'Last Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!isValidEmail(form.email)) errs.email = 'Invalid email format';
    if (!form.inquiryType) errs.inquiryType = 'Please select an inquiry type';
    if (!form.message.trim()) errs.message = 'Message is required';
    if (isBugReport && !form.whatTried.trim()) errs.whatTried = 'This field is required for bug reports';
    if (isBugReport && !form.whatHappened.trim()) errs.whatHappened = 'This field is required for bug reports';
    if (!form.gdprConsent) errs.gdprConsent = 'You must agree to the Privacy Policy and Terms of Service';
    return errs;
  }

  const errors = getErrors();
  const isFormValid = Object.keys(errors).length === 0;

  function touch(field: keyof FormState) {
    setTouched(prev => ({ ...prev, [field]: true }));
  }

  function touchAll() {
    const allFields: (keyof FormState)[] = ['firstName', 'lastName', 'email', 'inquiryType', 'message', 'gdprConsent'];
    if (isBugReport) allFields.push('whatTried', 'whatHappened');
    const t: Partial<Record<keyof FormState, boolean>> = {};
    allFields.forEach(f => { t[f] = true; });
    setTouched(t);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    touchAll();
    if (!isFormValid) return;
    setLoading(true);
    setError('');

    try {
      let res: Response;
      try {
        res = await fetch('/api/contact/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: form.firstName,
            lastName: form.lastName,
            fullName: `${form.firstName.trim()} ${form.lastName.trim()}`,
            email: form.email,
            company: form.company,
            phone: form.phone,
            inquiryType: form.inquiryType,
            message: form.message,
            whatTried: form.whatTried,
            whatHappened: form.whatHappened,
            browser: form.browser,
            device: form.device,
            screenshotUrl: form.screenshotUrl,
            gdprConsent: form.gdprConsent,
            marketingConsent: form.marketingConsent,
          }),
        });
      } catch (networkErr) {
        console.error('[Contact] Network error:', networkErr);
        setError('Connection issue. Please try again.');
        return;
      }

      let data: { success?: boolean; ticketRef?: string; guestToken?: string; error?: string; field?: string };
      try {
        data = await res.json();
      } catch (parseErr) {
        console.error('[Contact] Response parse error:', parseErr);
        setError('Server error. Please try later.');
        return;
      }

      console.log('[Contact] Response:', { status: res.status, data });

      if (!res.ok) {
        // Log full error for debugging
        console.error('[Contact] Submission failed:', { status: res.status, error: data.error, field: data.field });

        if (res.status >= 500) {
          setError('Server error. Please try later.');
        } else if (res.status === 400 && data.field) {
          // Validation error with specific field
          setError(data.error || 'Please check your form fields and try again.');
        } else if (res.status === 400) {
          setError(data.error || 'Please check your form fields and try again.');
        } else {
          setError(data.error || 'Something went wrong. Please try again.');
        }
        return;
      }

      setSuccessData({ ticketRef: data.ticketRef!, email: form.email, guestToken: data.guestToken });
      setSuccess(true);
    } catch (err: unknown) {
      console.error('[Contact] Unexpected error:', err);
      setError('Connection issue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const infoCards = [
    { icon: MapPin, label: 'Headquarters', value: 'Warsaw, Poland', href: null },
    { icon: Clock, label: 'Response Time', value: 'Within 24-48 hours on business days', href: null },
  ];

  function FieldError({ field }: { field: keyof FieldErrors }) {
    if (!touched[field] || !errors[field]) return null;
    return <p style={{ fontSize: '11px', color: '#F87171', marginTop: '4px' }}>{errors[field]}</p>;
  }

  return (
    <div style={{ background: '#060B14', minHeight: '100vh' }}>
      {/* Hero */}
      <section style={{ padding: '80px 40px 60px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 65% 45% at 50% 0%, rgba(249,115,22,0.09), transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '44px 44px', WebkitMaskImage: 'radial-gradient(ellipse 75% 75% at 50% 40%, black, transparent)', maskImage: 'radial-gradient(ellipse 75% 75% at 50% 40%, black, transparent)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: '700px', margin: '0 auto' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, color: '#F97316', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '16px' }}>Contact</span>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1.05, color: 'white', marginBottom: '14px' }}>Get in Touch</h1>
          <p style={{ fontSize: '16px', color: '#94A3B8', lineHeight: 1.65 }}>Have a question or need help? Fill in the form and we will get back to you within 24-48 hours on business days.</p>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ padding: '0 40px 80px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px', paddingTop: '60px' }}>
          {/* Left Column */}
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'white', marginBottom: '8px' }}>Get in Touch</h2>
            <p style={{ fontSize: '14px', color: '#64748B', marginBottom: '32px', lineHeight: 1.6 }}>Have a question or need help? Fill in the form and we will get back to you within 24-48 hours on business days.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {infoCards.map((card) => (
                <div key={card.label} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '16px' }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '9px', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <card.icon size={17} style={{ color: '#F97316' }} />
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>{card.label}</p>
                    {card.href ? (
                      <a href={card.href} style={{ fontSize: '13px', color: '#F97316', fontWeight: 600, textDecoration: 'none' }}>{card.value}</a>
                    ) : (
                      <p style={{ fontSize: '13px', color: '#94A3B8', lineHeight: 1.5 }}>{card.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column — Form */}
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'white', marginBottom: '24px' }}>Send us a Message</h2>

            {success && successData ? (
              <div style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '16px', padding: '40px', textAlign: 'center' }}>
                <CheckCircle2 size={52} style={{ color: '#4ADE80', margin: '0 auto 16px' }} />
                <h3 style={{ fontSize: '20px', fontWeight: 700, color: 'white', marginBottom: '8px' }}>Message sent successfully</h3>
                <p style={{ fontSize: '14px', color: '#94A3B8', marginBottom: '16px' }}>We have received your message and sent a confirmation to <strong style={{ color: '#CBD5E1' }}>{successData.email}</strong>.</p>
                <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '8px' }}>Your ticket reference:</p>
                <p style={{ fontFamily: 'monospace', fontSize: '20px', fontWeight: 700, color: '#F97316', marginBottom: '20px' }}>{successData.ticketRef}</p>
                <p style={{ fontSize: '13px', color: '#94A3B8', marginBottom: '24px' }}>We will respond within 24-48 hours on business days.</p>
                {successData.guestToken ? (
                  <a
                    href={`/support/ticket?ref=${encodeURIComponent(successData.ticketRef)}&token=${encodeURIComponent(successData.guestToken)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-block', background: '#F97316', color: 'white', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '14px', marginBottom: '16px' }}
                  >
                    Track Your Ticket
                  </a>
                ) : (
                  <a
                    href="/dashboard/support"
                    style={{ display: 'inline-block', background: '#F97316', color: 'white', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '14px', marginBottom: '16px' }}
                  >
                    View in Dashboard
                  </a>
                )}
                <br />
                <button onClick={() => { setSuccess(false); setSuccessData(null); setTouched({}); setForm(prev => ({ ...prev, firstName: '', lastName: '', email: '', company: '', phone: '', inquiryType: '', message: '', whatTried: '', whatHappened: '', screenshotUrl: '', gdprConsent: false, marketingConsent: false })); }} style={{ fontSize: '13px', color: '#64748B', background: 'none', border: 'none', cursor: 'pointer', marginTop: '8px' }}>
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Row 1: First Name + Last Name */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={labelStyle}>First Name *</label>
                    <input
                      type="text"
                      value={form.firstName}
                      onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))}
                      onBlur={() => touch('firstName')}
                      placeholder="First name"
                      style={touched.firstName && errors.firstName ? inputErrorStyle : inputStyle}
                    />
                    <FieldError field="firstName" />
                  </div>
                  <div>
                    <label style={labelStyle}>Last Name *</label>
                    <input
                      type="text"
                      value={form.lastName}
                      onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))}
                      onBlur={() => touch('lastName')}
                      placeholder="Last name"
                      style={touched.lastName && errors.lastName ? inputErrorStyle : inputStyle}
                    />
                    <FieldError field="lastName" />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label style={labelStyle}>Email Address *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    onBlur={() => touch('email')}
                    placeholder="you@company.com"
                    style={touched.email && errors.email ? inputErrorStyle : inputStyle}
                  />
                  <FieldError field="email" />
                </div>

                {/* Row 2: Company + Phone */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={labelStyle}>Company <span style={{ color: '#475569', fontWeight: 400 }}>(optional)</span></label>
                    <input type="text" value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} placeholder="Your company" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone <span style={{ color: '#475569', fontWeight: 400 }}>(optional)</span></label>
                    <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 234 567 8900" style={inputStyle} />
                  </div>
                </div>

                {/* Inquiry Type */}
                <div>
                  <label style={labelStyle}>Inquiry Type *</label>
                  <select
                    value={form.inquiryType}
                    onChange={e => setForm(p => ({ ...p, inquiryType: e.target.value }))}
                    onBlur={() => touch('inquiryType')}
                    style={touched.inquiryType && errors.inquiryType ? { ...inputErrorStyle, appearance: 'none' } : { ...inputStyle, appearance: 'none' }}
                  >
                    <option value="" disabled style={{ background: '#0F172A' }}>-- Select inquiry type --</option>
                    {INQUIRY_TYPES.map(t => <option key={t} value={t} style={{ background: '#0F172A' }}>{t}</option>)}
                  </select>
                  <FieldError field="inquiryType" />
                </div>

                {/* Message */}
                <div>
                  <label style={labelStyle}>Message *</label>
                  <textarea
                    value={form.message}
                    onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                    onBlur={() => touch('message')}
                    placeholder="Please describe your inquiry in detail. The more information you provide, the faster we can help."
                    style={touched.message && errors.message ? { ...inputErrorStyle, minHeight: '140px', resize: 'vertical' } : { ...inputStyle, minHeight: '140px', resize: 'vertical' }}
                  />
                  <FieldError field="message" />
                </div>

                {/* Conditional Bug Report Fields */}
                {isBugReport && (
                  <div style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '10px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <p style={{ fontSize: '12px', fontWeight: 600, color: '#F87171', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Bug Report Details</p>
                    <div>
                      <label style={labelStyle}>What were you trying to do? *</label>
                      <textarea
                        value={form.whatTried}
                        onChange={e => setForm(p => ({ ...p, whatTried: e.target.value }))}
                        onBlur={() => touch('whatTried')}
                        placeholder="Describe the action you were attempting..."
                        style={touched.whatTried && errors.whatTried ? { ...inputErrorStyle, minHeight: '80px', resize: 'vertical' } : { ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                      />
                      <FieldError field="whatTried" />
                    </div>
                    <div>
                      <label style={labelStyle}>What happened instead? *</label>
                      <textarea
                        value={form.whatHappened}
                        onChange={e => setForm(p => ({ ...p, whatHappened: e.target.value }))}
                        onBlur={() => touch('whatHappened')}
                        placeholder="Describe the unexpected behavior..."
                        style={touched.whatHappened && errors.whatHappened ? { ...inputErrorStyle, minHeight: '80px', resize: 'vertical' } : { ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                      />
                      <FieldError field="whatHappened" />
                    </div>
                    <div>
                      <label style={labelStyle}>Your browser — detected automatically</label>
                      <input type="text" value={form.browser} readOnly style={{ ...inputStyle, opacity: 0.6, cursor: 'default' }} />
                    </div>
                    <div>
                      <label style={labelStyle}>Device — detected automatically</label>
                      <input type="text" value={form.device} readOnly style={{ ...inputStyle, opacity: 0.6, cursor: 'default' }} />
                    </div>
                    <div>
                      <label style={labelStyle}>Screenshot URL — optional. Upload to Imgur or similar and paste the link.</label>
                      <input type="text" value={form.screenshotUrl} onChange={e => setForm(p => ({ ...p, screenshotUrl: e.target.value }))} placeholder="https://imgur.com/..." style={inputStyle} />
                    </div>
                  </div>
                )}

                {/* Consent */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
                  <div>
                    <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={form.gdprConsent}
                        onChange={e => { setForm(p => ({ ...p, gdprConsent: e.target.checked })); touch('gdprConsent'); }}
                        style={{ marginTop: '2px', accentColor: '#F97316', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '13px', color: '#94A3B8', lineHeight: 1.5 }}>
                        I have read and agree to the{' '}
                        <Link href="/privacy-policy" style={{ color: '#F97316', textDecoration: 'none' }}>Privacy Policy</Link>
                        {' '}and{' '}
                        <Link href="/terms-of-service" style={{ color: '#F97316', textDecoration: 'none' }}>Terms of Service</Link>. *
                      </span>
                    </label>
                    {touched.gdprConsent && errors.gdprConsent && (
                      <p style={{ fontSize: '11px', color: '#F87171', marginTop: '4px', marginLeft: '22px' }}>{errors.gdprConsent}</p>
                    )}
                  </div>
                  <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={form.marketingConsent}
                      onChange={e => setForm(p => ({ ...p, marketingConsent: e.target.checked }))}
                      style={{ marginTop: '2px', accentColor: '#F97316', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '13px', color: '#94A3B8', lineHeight: 1.5 }}>
                      I would like to receive product updates, tips, and marketing emails from Incade. I can unsubscribe at any time.
                    </span>
                  </label>
                </div>

                {/* Error */}
                {error && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', padding: '12px 14px' }}>
                    <AlertCircle size={16} style={{ color: '#F87171', flexShrink: 0 }} />
                    <p style={{ fontSize: '13px', color: '#F87171', margin: 0 }}>Something went wrong. Please try again.</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: !loading ? '#F97316' : 'rgba(249,115,22,0.3)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: 700,
                    cursor: !loading ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'background 0.2s',
                  }}
                >
                  {loading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
                  {loading ? 'Sending...' : 'Send Message'}
                </button>

                <p style={{ fontSize: '12px', color: '#475569', textAlign: 'center', lineHeight: 1.5 }}>
                  By submitting this form, your message will be securely stored and you will receive a ticket reference number by email so you can track your inquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
